import type {
  SDM,
  TemplateVersion,
  MeasurementUnit,
  ItemDef,
  MethodDef,
  ConditionDef,
  EventDef,
  FormDef,
  ItemGroupDef,
  CodeList,
  GlobalVariables,
  BasicDefinition,
  SDMHistoryEntry,
  SDMMapping,
} from '../types/sdm';

import type {
  SdmDocument,
  SdmTemplateVersion,
  SdmMeasurementUnit,
  SdmItemDef,
  SdmMethodDef,
  SdmConditionDef,
  SdmEventDef,
  SdmFormDef,
  SdmItemGroupDef,
  SdmCodeList,
  SdmMapping,
} from '../types/sdm-wire';

import { bumpMinor, compareVersionOids, mergeVersions, resolveVersionChain } from './sdm-utils';

export function deserializeFromSdmDocument(doc: SdmDocument): SDM {
  const building = doc.SDM.Building;
  const publishedVersions: TemplateVersion[] = building.TemplateVersion.map(deserializeTemplateVersion);

  const truePublished = publishedVersions.filter((v: any) => v._meta);
  const lastTruePub = truePublished[truePublished.length - 1];
  const draftVersionId = lastTruePub ? bumpMinor(lastTruePub.version) : 'MDV.1';

  const savedDraftData = (building as any)._naomiDraft;
  let draftEntry: TemplateVersion;
  if (savedDraftData) {
    const savedDraft = deserializeTemplateVersion(savedDraftData);
    draftEntry = { ...savedDraft, status: 'draft', version: draftVersionId, IncludeOID: undefined };
  } else {
    const resolved: TemplateVersion = publishedVersions.length > 1
      ? resolveVersionChain(publishedVersions)!
      : (publishedVersions[0] ?? blankVersion());
    draftEntry = { ...resolved, status: 'draft', version: draftVersionId, IncludeOID: undefined };
  }

  const sortedForHistory = [...publishedVersions].sort((a, b) =>
    compareVersionOids(a.version, b.version),
  );
  let histAcc: TemplateVersion | null = null;
  const historyAsc: SDMHistoryEntry[] = sortedForHistory.map((pv) => {
    histAcc = histAcc ? mergeVersions(histAcc, pv) : { ...pv };
    return {
      version: pv.version,
      status: 'published',
      author: pv._meta?.author ?? '',
      date: pv._meta?.date ?? '',
      note: pv._meta?.note ?? 'Published version',
      changes: 0,
      questionCount: histAcc.ItemDef?.length ?? 0,
      sectionCount: histAcc.ItemGroupDef?.length ?? 0,
    };
  });
  const history = historyAsc.reverse();

  const globalVariables = deserializeGlobalVariables(building.GlobalVariables);
  const withFormName = (v: TemplateVersion): TemplateVersion => ({
    ...v,
    FormDef: v.FormDef.map(f => ({ ...f, Name: globalVariables.Name || f.Name })),
  });

  return {
    GlobalVariables: globalVariables,
    BasicDefinition: deserializeBasicDefinition(building.BasicDefinition),
    TemplateVersion: [draftEntry, ...publishedVersions].map(withFormName),
    history,
    draft: {
      version: draftVersionId,
      basedOn: lastTruePub?.version ?? '',
      status: 'draft',
      changedSince: [],
    },
  };
}


function blankVersion(): TemplateVersion {
  return {
    version: 'MDV.1',
    status: 'published',
    Protocol: { EventRef: [] },
    Images: [],
    EventDef: [],
    FormDef: [],
    ItemGroupDef: [],
    ItemDef: [],
    MethodDef: [],
    CodeList: [],
    ConditionDef: [],
  };
}

function deserializeGlobalVariables(gv: any): GlobalVariables {
  return {
    Name: gv.Name ?? '',
    Description: gv.Description ?? '',
    tradeName: gv.Name ?? '',
    tradeCategory: gv.tradeCategory ?? '',
    TemplateType: undefined,
  };
}

function deserializeBasicDefinition(bd: any): BasicDefinition {
  return {
    MeasurementUnit: (bd?.MeasurementUnit ?? []).map(deserializeMeasurementUnit),
  };
}

function deserializeMeasurementUnit(mu: SdmMeasurementUnit): MeasurementUnit {
  return {
    OID: mu.OID,
    Name: mu.Name,
    Symbol: mu.Symbol?.TranslatedText?.text ?? '',
  };
}

function deserializeTemplateVersion(tv: SdmTemplateVersion): TemplateVersion {
  const allGroups = (tv.ItemGroupDef ?? []).map(deserializeItemGroupDef);
  const { topLevel, formGroupOids } = reconstructSubgroups(allGroups, tv.MethodDef ?? [], tv.FormDef ?? []);

  const formDefs = (tv.FormDef ?? []).map((f) => {
    const filtered = (f.ItemGroupRef ?? []).filter(
      (ref: any) => !formGroupOids.has(ref.ItemGroupOID),
    );
    return deserializeFormDef({ ...f, ItemGroupRef: filtered } as SdmFormDef);
  });

  return {
    version: tv.OID,
    status: 'published',
    Protocol: tv.Protocol ?? { EventRef: [] },
    Images: tv.Images ?? [],
    EventDef: (tv.EventDef ?? []).map(deserializeEventDef),
    FormDef: formDefs,
    ItemGroupDef: topLevel,
    ItemDef: (tv.ItemDef ?? []).map(deserializeItemDef),
    MethodDef: (tv.MethodDef ?? []).map(deserializeMethodDef),
    CodeList: (tv.CodeList ?? []).map(deserializeCodeList),
    ConditionDef: (tv.ConditionDef ?? []).map(deserializeConditionDef),
    ...(tv.Mappings?.length ? { Mappings: tv.Mappings.map(m => normalizeSdmMapping(m, tv._naomiMeta?.date)) } : {}),
    ...(tv._naomiMeta ? { _meta: tv._naomiMeta } : {}),
  };
}

function reconstructSubgroups(
  groups: ItemGroupDef[],
  rawMethodDefs: SdmMethodDef[],
  rawFormDefs: SdmFormDef[],
): { topLevel: ItemGroupDef[]; formGroupOids: Set<string> } {
  const byOid = new Map<string, ItemGroupDef>(groups.map((g) => [g.OID, g]));
  const subgroupOids = new Set<string>();

  for (const g of groups) {
    const parentOid = (g as any)._naomiParentOID as string | undefined;
    if (!parentOid) continue;

    subgroupOids.add(g.OID);
    const parent = byOid.get(parentOid);
    if (!parent) continue;

    const gCopy: any = { ...g };
    delete gCopy._naomiParentOID;
    if (gCopy.Repeating === 'Yes' && gCopy.RepeatFromOID && !gCopy.MethodOID) {
      const rfOid = gCopy.RepeatFromOID as string;
      const match = rawMethodDefs.find(
        (m) => m.FormalExpression?.text?.includes('RepeatCount') && m.FormalExpression?.text?.includes(rfOid),
      );
      if (match) gCopy.MethodOID = match.OID;
    }

    for (const f of rawFormDefs) {
      const igRef = (f.ItemGroupRef ?? []).find((ref: any) => ref.ItemGroupOID === g.OID);
      if (!igRef) continue;
      if (!gCopy.ConditionOID && igRef.CollectionExceptionConditionOID) {
        gCopy.ConditionOID = igRef.CollectionExceptionConditionOID;
      }
      if (!gCopy.SummarizeInstances && (igRef as any).SummarizeInstances) {
        gCopy.SummarizeInstances = (igRef as any).SummarizeInstances;
      }
      break;
    }
    byOid.set(g.OID, gCopy);
    if (!parent.Subgroups) parent.Subgroups = [];
    parent.Subgroups.push(gCopy);
  }

  if (subgroupOids.size > 0) {
    return { topLevel: groups.filter((g) => !subgroupOids.has(g.OID)), formGroupOids: subgroupOids };
  }

  const questionOwner = new Map<string, string>();
  for (const g of groups) {
    for (const ref of g.ItemRef) {
      questionOwner.set(ref.ItemOID, g.OID);
    }
  }

  const groupRefMethodOid = new Map<string, string>();
  for (const f of rawFormDefs) {
    for (const ref of (f.ItemGroupRef ?? []) as any[]) {
      if (ref.MethodOID) groupRefMethodOid.set(ref.ItemGroupOID, ref.MethodOID);
    }
  }

  const methExprByOid = new Map<string, string>(
    rawMethodDefs.map((m) => [m.OID, m.FormalExpression?.text ?? '']),
  );

  for (const g of groups) {
    const methodOID = ((g as any).MethodOID as string | undefined) ?? groupRefMethodOid.get(g.OID);
    if (!methodOID) continue;

    const expr = methExprByOid.get(methodOID) ?? '';
    if (!expr.includes('RepeatCount')) continue;

    const m = expr.match(/!\(\s*(I\.\d+)\s*:\s*RepeatCount\s*\)/);
    if (!m) continue;
    const countOID = m[1];

    const parentOid = questionOwner.get(countOID);
    if (!parentOid || parentOid === g.OID) continue;

    const parent = byOid.get(parentOid);
    // Non-repeating nested subgroups cannot be detected without _naomiParentOID metadata;
    // SDM documents predating that metadata will have such groups promoted to top level.
    if (!parent || parent.Repeating !== 'Yes') continue;

    subgroupOids.add(g.OID);
    const withRepeatFrom: any = { ...g, RepeatFromOID: countOID };
    for (const f of rawFormDefs) {
      const igRef = (f.ItemGroupRef ?? []).find((ref: any) => ref.ItemGroupOID === g.OID);
      if ((igRef as any)?.SummarizeInstances) {
        withRepeatFrom.SummarizeInstances = (igRef as any).SummarizeInstances;
        break;
      }
    }
    if (!parent.Subgroups) parent.Subgroups = [];
    parent.Subgroups.push(withRepeatFrom as ItemGroupDef);
  }
  const topLevel = groups.filter((g) => !subgroupOids.has(g.OID));
  return { topLevel, formGroupOids: subgroupOids };
}

function deserializeEventDef(e: SdmEventDef): EventDef {
  return {
    OID: e.OID,
    Name: e.Description?.TranslatedText?.text ?? e.Name,
    Type: e.Type || 'Common',
    FormRef: e.FormRef ?? [],
  };
}

function deserializeFormDef(f: SdmFormDef): FormDef {
  return {
    OID: f.OID,
    Name: f.Description?.TranslatedText?.text ?? f.Name,
    ItemGroupRef: (f.ItemGroupRef ?? []).map((ref) => ({ ...ref })) as any,
  };
}

function deserializeItemGroupDef(g: SdmItemGroupDef): ItemGroupDef {
  return {
    OID: g.OID,
    Name: g.Description?.TranslatedText?.text ?? g.Name,
    Repeating: g.Repeating,
    ItemRef: (g.ItemRef ?? []).map((ref) => ({ ...ref })) as any,
    ...(g.MethodOID                        ? { MethodOID:         g.MethodOID }                        : {}),
    ...((g as any)._naomiParentOID         ? { _naomiParentOID:   (g as any)._naomiParentOID }         : {}),
    ...((g as any)._naomiRepeatFromOID     ? { RepeatFromOID:     (g as any)._naomiRepeatFromOID }     : {}),
    ...(g.ConditionOID                     ? { ConditionOID:      g.ConditionOID }                     : {}),
  };
}

function deserializeItemDef(item: SdmItemDef): ItemDef {
  const naomiItem: ItemDef = {
    OID: item.OID,
    Name: item.OID,
    DataType: item.DataType as any,
    Question: item.Question,
  };

  if (item.MeasurementOid) naomiItem.MeasurementOid = item.MeasurementOid;
  if (item.CodeListRef)    naomiItem.CodeListRef    = item.CodeListRef;
  if (item.MetaKey)        naomiItem.MetaKey        = item.MetaKey;
  if (item.ImageUrl)       naomiItem.ImageUrl       = item.ImageUrl;
  if (item.ThumbnailUrl)   naomiItem.ThumbnailUrl   = item.ThumbnailUrl;

  if (item.AutoFillRef) (naomiItem as any).AutoFillRef = item.AutoFillRef;

  if (item.RangeCheck?.length) {
    naomiItem.RangeCheck = item.RangeCheck as ItemDef['RangeCheck'];
  }

  return naomiItem;
}

function deserializeMethodDef(m: SdmMethodDef): MethodDef {
  const descText = m.Description?.TranslatedText?.text ?? '';
  const isOidLabel = descText === m.OID;

  const naomiM: MethodDef = {
    OID: m.OID,
    FormalExpression: m.FormalExpression,
    humanReadable: isOidLabel ? undefined : descText,
    kind: m.FormalExpression?.text?.includes('RepeatCount') ? 'repeat' : 'calc',
  };

  if (m.MethodType) (naomiM as any).MethodType = m.MethodType;
  return naomiM;
}

function deserializeConditionDef(c: SdmConditionDef): ConditionDef {
  const descText = c.Description?.TranslatedText?.text ?? '';
  return {
    OID: c.OID,
    FormalExpression: c.FormalExpression,
    humanReadable: descText === c.OID ? undefined : descText,
    ...(c._naomi_triggerOid !== undefined && { triggerOid: c._naomi_triggerOid }),
    ...(c._naomi_op         !== undefined && { op:         c._naomi_op }),
    ...(c._naomi_val        !== undefined && { val:        c._naomi_val }),
    ...(c._naomi_expression !== undefined && { expression: c._naomi_expression }),
  };
}

function deserializeCodeList(cl: SdmCodeList): CodeList {
  return {
    OID: cl.OID,
    DataType: 'text',
    CodeListItem: cl.CodeListItem ?? [],
  };
}

function normalizeSdmMapping(m: SdmMapping, fallbackDate?: string): SDMMapping {
  const stable = m.LastUpdated || fallbackDate || '1970-01-01T00:00:00.000Z';
  return {
    Name: m.Name,
    Source: m.Source,
    LastUpdated: stable,
    Description: m.Description,
    Versions: (m.Versions ?? []).map(v => ({
      VersionOID: v.VersionOID,
      VersionName: v.VersionName,
      CreatedBy: v.CreatedBy,
      CreatedDate: v.CreatedDate || stable,
      ItemGroupDef: v.ItemGroupDef,
    })),
  };
}

